import { heart, loan, newspaper, play } from '@dnb/eufemia/icons';
import { useMedia } from '@dnb/eufemia/shared';
import { FeatureFlagDto } from '@portals/shared/portal/FeatureFlagDto';
import { useMemo } from 'react';

import useAuth from '@/hooks/useAuth';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useUser } from '@/hooks/useUser';

import NavigationButton, { NavigationButtonProps } from '../NavigationButton';

import style from './index.module.css';

function navigationUris(featureFlags: FeatureFlagDto, isDnbUser: boolean): NavigationButtonProps[] {
  const links: NavigationButtonProps[] = [
    {
      title: 'Documentation',
      icon: newspaper,
      links: [
        {
          title: 'Regulatory APIs',
          to: '/explorer/apis/regulatory',
          isVisible: true,
        },
        {
          title: 'Commercial APIs',
          to: '/explorer/apis/commercial',
          isVisible: true,
        },
        {
          title: 'Internal APIs',
          to: '/explorer/apis/internal',
          isVisible: isDnbUser,
        },
        {
          title: 'Events',
          to: '/explorer/events',
          subTitle: 'Discover events',
          isVisible: isDnbUser,
        },
        {
          title: 'API contracts',
          to: '/api-contracts',
          subTitle: 'All API contracts in Gitlab',
          isVisible: featureFlags.ENABLE_COMMON_REPO_API && isDnbUser,
        },
        {
          title: 'Scope overview',
          to: '/scope-overview',
          subTitle: 'Scopes available in ciam',
          isVisible: featureFlags.ENABLE_SCOPE_OVERVIEW && isDnbUser,
        },
      ],
    },
    {
      title: 'Get Started',
      icon: play,
      links: [
        {
          title: isDnbUser ? 'For non-DNB users' : 'Get Started',
          to: '/getting-started',
          isVisible: true,
        },
        {
          title: 'Providing APIs',
          to: '/getting-started/api-providers',
          isVisible: isDnbUser,
        },
        {
          title: 'Consuming APIs',
          to: '/getting-started/dnb-internal-consumers',
          isVisible: isDnbUser,
        },
        {
          title: 'OAuth2 Authentication',
          to: '/getting-started/oauth2-authentication',
          isVisible: isDnbUser,
        },
      ],
    },
    {
      title: 'Digital Channel Statistics',
      icon: loan,
      links: [
        {
          title: 'Digital Channel Statistics',
          to: '/channel-statistics',
          isVisible: true,
        },
      ],
    },
    {
      title: 'Support',
      icon: heart,
      links: [
        { title: 'API status', to: '/status', isVisible: true },
        { title: 'News & updates', to: '/news', isVisible: true },
        {
          title: 'Help center',
          to: '/help-center',
          subTitle: 'Need help or want to report something?',
          isVisible: true,
        },
        {
          title: 'FAQ',
          to: '/help-center/faq/psd2',
          subTitle: 'Find answers to your questions in our FAQ',
          isVisible: true,
        },
        {
          title: 'JWT Validator',
          to: '/jwt-validator',
          subTitle: 'Decode and validate JSON Web Tokens',
          isVisible: isDnbUser,
        },
      ],
    },
  ];

  const linkConstructor: NavigationButtonProps[] = links.map(
    (linkObject): NavigationButtonProps => ({
      links: linkObject.links!.filter((link) => link.isVisible),
      title: linkObject.title,
      icon: linkObject.icon,
    }),
  );

  return linkConstructor;
}

export default function NavigationButtonMenu() {
  const { featureFlags } = useFeatureFlags();
  const { isLarge } = useMedia();
  const { isInternal } = useAuth();
  const { isLoggedIn } = useUser();
  const isDnbUser = isInternal && isLoggedIn;
  const menuLinks = useMemo(() => navigationUris(featureFlags, isDnbUser), [featureFlags, isDnbUser]);

  return (
    <div className={isLarge ? style['MenuLinks'] : style['hamburgerDialog']}>
      {menuLinks.map((uri) => (
        <NavigationButton icon={uri.icon} key={uri.title} links={uri.links} title={uri.title} />
      ))}
    </div>
  );
}
